<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  modelValue?: boolean;
  disabled?: boolean;
  label?: string;
  name?: string;
  checkboxColor?: string;      // button background
  checkboxCheckColor?: string; // checkmark stroke, default #ffffff
  checkboxBorderColor?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isChecked = computed(() => props.modelValue || false);

const isDisabled = computed(() => props.disabled || false);

const checkboxButtonStyle = computed(() => {
  const style: Record<string, string> = {};
  style.borderColor = props.checkboxBorderColor || 'var(--checkbox-border, #CFCFCF)';
  return style;
});

function handleChange() {
  if (isDisabled.value) return;
  emit('update:modelValue', !isChecked.value);
}
</script>
<template>
  <div 
    class="checkbox-item"
    :class="{ disabled: isDisabled, checked: isChecked }"
    @click="handleChange"
  >
    <input
      type="checkbox"
      :name="name"
      :checked="isChecked"
      :disabled="isDisabled"
      class="checkbox-input"
      @change="handleChange"
    />
    <div 
      class="checkbox-button"
    >
      <div
        class="checkbox-check"
        :class="{ visible: isChecked }"
        :style="props.checkboxCheckColor ? { color: props.checkboxCheckColor } : {}"
      >
        <svg width="26" height="26" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 6L5 9L10 3"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
    <span class="checkbox-label" v-if="label">{{ label }}</span>
  </div>
</template>

<style scoped>
.checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  /* padding: 8px; */
  border-radius: 6px;
  transition: background-color 0.2s ease;
  position: relative;
}

.checkbox-item:hover:not(.disabled) {
  /* background-color: var(--nav-hover); */
}

.checkbox-item.checked {
  /* background-color: var(--color-primary-light)); */
}

.checkbox-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.checkbox-button {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  min-width: 40px;
  min-height: 40px;
  max-width: 40px;
  max-height: 40px;
  padding: 5px;
  border-radius: 5px;
  border: 1pt solid var(--checkbox-border, #CFCFCF);
  transition: border-color 0.2s ease, background-color 0.2s ease;
  flex-shrink: 0;
  background-color: var(--checkbox-bg);
}

.checkbox-item.disabled .checkbox-button {
  border-color: var(--text-secondary);
}

.checkbox-check {
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--checkbox-check-color, #52CFBB);
}

.checkbox-check.visible {
  opacity: 1;
}

.checkbox-label {
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
  user-select: none;
  flex: 1;
}

.checkbox-item.disabled .checkbox-label {
  color: var(--text-secondary);
}
</style>